import { useEffect, useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { Gateway, Task } from "pages/ProjectManagement/ProjectManagement.types";
import { parseExistingGatewayItemDate } from "helpers/parseGatewayDate";
import { isGatewayExpandable, fetchPrintContent } from "./GatewaySummary.utils";
import { HeaderTaskState, ExpandState, PrintState, ParsedDates } from "./GatewaySummary.types";
import _ from "lodash";

/**
 * Hook to manage gateway expand/collapse state
 * @param gateway - Gateway object with NA settings
 * @param toBeExpanded - Whether gateway should be expanded on load
 * @returns Object containing expand state and handlers
 */
export const useGatewayExpand = (gateway: Gateway, toBeExpanded?: boolean): ExpandState => {
  const [expand, setExpand] = useState<boolean>(false);

  useEffect(() => {
    if (toBeExpanded && isGatewayExpandable(gateway)) {
      setExpand(true);
    } else if (expand && !toBeExpanded) {
      setExpand(false);
    }
  }, [toBeExpanded]);

  const handleExpand = () => {
    if (isGatewayExpandable(gateway)) {
      setExpand(!expand);
    }
  };

  return {
    expand,
    setExpand,
    handleExpand,
  };
};

/**
 * Hook to manage and find header task within gateway tasks
 * @param gateway - Gateway object containing tasks
 * @returns Object containing header task and index
 */
export const useHeaderTask = (gateway: Gateway): HeaderTaskState => {
  const [headerGateway, setHeaderGateway] = useState<Task | null>(null);
  const [headerGatewayIndex, setHeaderGatewayIndex] = useState<number | null>(null);

  useEffect(() => {
    if (gateway.taskHeadersID && gateway.tasks && gateway.tasks.length > 0) {
      const match = gateway.tasks.filter((item) => item.taskTemplatesID === gateway.taskHeadersID);

      if (match.length === 1) {
        const index = gateway.tasks.indexOf(match[0]);

        if (!_.isEqual(headerGateway, match[0])) {
          setHeaderGateway(_.cloneDeep(match[0]));
        }

        if (index !== headerGatewayIndex) {
          setHeaderGatewayIndex(index);
        }
      }
    } else {
      setHeaderGateway(null);
      setHeaderGatewayIndex(null);
    }
  }, [gateway]);

  return {
    headerGateway,
    headerGatewayIndex,
  };
};

/**
 * Hook to manage print functionality and PDF content
 * @param projectID - Project ID for fetching content
 * @param gatewayId - Gateway ID for fetching content
 * @param gatewayTemplateId - Gateway Template ID for fetching content
 * @returns Object containing print state, ref, and handler
 */
export const usePrintGateway = (
  projectID: number,
  gatewayId: string | number,
  gatewayTemplateId?: number
): PrintState => {
  const [printContent, setPrintContent] = useState<Gateway | null>(null);
  const componentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onBeforePrint: () => {},
    onBeforeGetContent: async () => {
      await fetchPrintContent(
        {
          projectId: projectID,
          gatewayId: gatewayId as number,
          gatewayTemplateId: gatewayTemplateId as number,
        },
        setPrintContent
      );
    },
  });

  return {
    printContent,
    setPrintContent,
    componentRef,
    handlePrint,
  };
};

/**
 * Hook to parse and manage date state for task dates (Planned, Forecast, Actual)
 * @param task - Task object containing date strings
 * @returns Object containing parsed date objects
 */
export const useDateParsing = (task: Task): ParsedDates => {
  const [parsedPlannedDate, setParsedPlannedDate] = useState<Date | null>(null);
  const [parsedForecastDate, setParsedForecastDate] = useState<Date | null>(null);
  const [parsedActualDate, setParsedActualDate] = useState<Date | null>(null);

  useEffect(() => {
    if (task.plannedDate) {
      setParsedPlannedDate(parseExistingGatewayItemDate(task.plannedDate));
    } else {
      setParsedPlannedDate(null);
    }
  }, [task.plannedDate]);

  useEffect(() => {
    if (task.forecastDate) {
      setParsedForecastDate(parseExistingGatewayItemDate(task.forecastDate));
    } else {
      setParsedForecastDate(null);
    }
  }, [task.forecastDate]);

  useEffect(() => {
    if (task.actualDate) {
      setParsedActualDate(parseExistingGatewayItemDate(task.actualDate));
    } else {
      setParsedActualDate(null);
    }
  }, [task.actualDate]);

  return {
    parsedPlannedDate,
    parsedForecastDate,
    parsedActualDate,
  };
};
